import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
  Squares2X2Icon,
  Bars3BottomLeftIcon,
} from '@heroicons/react/24/outline';

/**
 * Risk Register (operator order 2026-09-11): every risk sits in one of the
 * organization's categories, and the register is organized by them. A list
 * grouped by category, or a board with one column per category where a risk
 * is dragged from one to another. A row opens a side panel; the full record
 * is one click further. Every category change is written to the audit log by
 * the server, with who, when, from and to.
 */
const API_BASE = process.env.REACT_APP_API_URL + '/api';

const STATUS_TONE = {
  open: 'bg-sky-50 text-sky-700', monitoring: 'bg-amber-50 text-amber-800',
  mitigating: 'bg-amber-50 text-amber-700', accepted: 'bg-slate-100 text-slate-600',
  transferred: 'bg-slate-100 text-slate-600', closed: 'bg-emerald-50 text-emerald-700',
};
const STATUS_LABEL = {
  open: 'Open', monitoring: 'Monitoring', mitigating: 'Mitigating',
  accepted: 'Accepted', transferred: 'Transferred', closed: 'Closed',
};
const BAND_TONE = {
  Critical: 'bg-red-600 text-white', High: 'bg-orange-500 text-white',
  Moderate: 'bg-yellow-500 text-white', Low: 'bg-emerald-600 text-white',
  'Tier A': 'bg-red-600 text-white', 'Tier B': 'bg-amber-500 text-white', 'Tier C': 'bg-emerald-600 text-white',
};
// One color per category, so the eye learns them. Keyed by the words in the name.
const CATEGORY_COLORS = [
  [/compliance|regulat/i, { dot: 'bg-orange-500', soft: 'bg-orange-50 text-orange-800 ring-orange-200' }],
  [/operational/i, { dot: 'bg-sky-500', soft: 'bg-sky-50 text-sky-800 ring-sky-200' }],
  [/financial/i, { dot: 'bg-emerald-500', soft: 'bg-emerald-50 text-emerald-800 ring-emerald-200' }],
  [/information security|cyber/i, { dot: 'bg-violet-500', soft: 'bg-violet-50 text-violet-800 ring-violet-200' }],
  [/clinical|quality/i, { dot: 'bg-teal-500', soft: 'bg-teal-50 text-teal-800 ring-teal-200' }],
  [/fraud|waste|abuse/i, { dot: 'bg-rose-500', soft: 'bg-rose-50 text-rose-800 ring-rose-200' }],
  [/third party|vendor/i, { dot: 'bg-indigo-500', soft: 'bg-indigo-50 text-indigo-800 ring-indigo-200' }],
  [/strateg/i, { dot: 'bg-fuchsia-500', soft: 'bg-fuchsia-50 text-fuchsia-800 ring-fuchsia-200' }],
  [/reputation/i, { dot: 'bg-amber-500', soft: 'bg-amber-50 text-amber-800 ring-amber-200' }],
];
const colorFor = (name) => (CATEGORY_COLORS.find(([re]) => re.test(name || '')) || [null, { dot: 'bg-slate-400', soft: 'bg-slate-100 text-slate-700 ring-slate-200' }])[1];
const UNCATEGORIZED = { id: '__none__', name: 'No category yet', description: 'Give each of these a category.' };

// Methodology aware level from a registered risk's own scores. Mirrors
// ScoreInput and RiskDetail so the register shows a real level even for
// intake promoted risks with no assessment instances.
export function computeRiskLevel(methodology, r, rpnScale = 10) {
  if (methodology === 'rpn_fmea') {
    const s = (r.severity_score || 0) * (r.occurrence_score || 0) * (r.detection_score || 0);
    const max = rpnScale * rpnScale * rpnScale;
    let band = 'Low';
    if (s > 0.5 * max) band = 'Critical'; else if (s > 0.3 * max) band = 'High'; else if (s > 0.1 * max) band = 'Moderate';
    return s > 0 ? { score: s, band } : { score: null, band: null };
  }
  if (methodology === 'inherent_residual') {
    const inh = (r.severity_score || 0) * (r.likelihood_score || 0) * (r.exposure_score || 0);
    const ca = ((Number(r.control_design_score) || 0) + (Number(r.control_operating_score) || 0)) / 2;
    const res = Math.max(inh * (1 - Math.min(ca / 4, 1)), 1);
    let tier = 'C';
    if (res >= 50) tier = 'A'; else if (res >= 30) tier = 'B';
    return inh > 0 ? { score: Math.round(res * 100) / 100, band: `Tier ${tier}` } : { score: null, band: null };
  }
  const imp = (r.impact_score || r.severity_score || 0) * (r.likelihood_score || 0);
  let band = 'Low';
  if (imp >= 16) band = 'Critical'; else if (imp >= 10) band = 'High'; else if (imp >= 5) band = 'Moderate';
  return imp > 0 ? { score: imp, band } : { score: null, band: null };
}
const BAND_RANK = { Critical: 4, 'Tier A': 4, High: 3, 'Tier B': 2, Moderate: 2, Low: 1, 'Tier C': 1 };

// Update cadence: the register leads with whether the current period's update
// is in. Quarterly by default; a risk flagged monthly is judged month by month.
export function quarterLabelOf(d) {
  const dt = d ? new Date(d) : new Date();
  return `Q${Math.floor(dt.getUTCMonth() / 3) + 1} ${dt.getUTCFullYear()}`;
}
const sameUtcMonth = (a, b) => a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth();
export function updateVerdict(risk, now = new Date()) {
  if (risk.status === 'closed') return { k: 'closed', label: 'Closed', tone: 'bg-slate-100 text-slate-500' };
  const last = risk.last_update_at ? new Date(risk.last_update_at) : null;
  if (risk.monthly_update_required) {
    if (last && sameUtcMonth(last, now)) return { k: 'current', label: 'Updated this month', tone: 'bg-emerald-50 text-emerald-700' };
    return { k: 'due', label: 'Monthly update due', tone: 'bg-red-50 text-red-700' };
  }
  const qNow = quarterLabelOf(now);
  if (last && quarterLabelOf(last) === qNow) return { k: 'current', label: `${qNow} update in`, tone: 'bg-emerald-50 text-emerald-700' };
  return { k: 'awaiting', label: `Awaiting ${qNow} update`, tone: 'bg-amber-50 text-amber-800' };
}

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const ownerText = (r) => (r.owner_names?.length ? r.owner_names.join(', ') : (r.owner_name || 'No owner'));
const initials = (name) => String(name || '?').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const LevelPill = ({ level }) => level?.band
  ? <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold whitespace-nowrap ${BAND_TONE[level.band] || 'bg-slate-200 text-slate-600'}`}>{level.band}</span>
  : <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-100 text-slate-400 whitespace-nowrap">Not scored</span>;
const VerdictChip = ({ v }) => <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${v.tone}`}>{v.label}</span>;
const StatusPill = ({ s }) => <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full whitespace-nowrap ${STATUS_TONE[s] || STATUS_TONE.open}`}>{STATUS_LABEL[s] || 'Open'}</span>;
const Avatar = ({ name }) => <span title={name} className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700 flex-none">{initials(name === 'No owner' ? '?' : name)}</span>;

const RiskLibrary = ({ onOpenRisk }) => {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedTier, setSelectedTier] = useState('');
  const [selectedOwner, setSelectedOwner] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [domains, setDomains] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [methodology, setMethodology] = useState('likelihood_impact_5x5');
  const [rpnScale, setRpnScale] = useState(10);
  const [scope, setScope] = useState('attention'); // attention | all
  const [layout, setLayout] = useState(() => { try { return localStorage.getItem('risk_register_layout') || 'list'; } catch { return 'list'; } });
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [openRiskId, setOpenRiskId] = useState(null);
  const [notice, setNotice] = useState('');
  const [dragging, setDragging] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  useEffect(() => { try { localStorage.setItem('risk_register_layout', layout); } catch { /* fine */ } }, [layout]);

  const fetchRisks = async (status = selectedStatus) => {
    try {
      setLoading(true); setError('');
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      const response = await fetch(`${API_BASE}/risk-assessment/risks?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) setRisks(data.risks); else setError(data.message || 'The register could not be loaded.');
    } catch (e) { setError(e.message || 'The register could not be loaded.'); }
    finally { setLoading(false); }
  };
  const fetchDomains = async () => {
    try { const r = await fetch(`${API_BASE}/risk-assessment/domains`, { credentials: 'include' }); const d = await r.json(); if (d.success) setDomains(d.domains || []); } catch { /* keep going */ }
  };
  const fetchDepartments = async () => {
    try { const r = await fetch(`${API_BASE}/departments`, { credentials: 'include' }); const d = await r.json(); if (Array.isArray(d)) setDepartments(d); } catch { /* keep going */ }
  };
  const fetchSettings = async () => {
    try {
      const r = await fetch(`${API_BASE}/risk-assessment/organization/settings`, { credentials: 'include' });
      const d = await r.json();
      if (d.success && d.settings) { if (d.settings.scoring_methodology) setMethodology(d.settings.scoring_methodology); if (d.settings.rpn_scale) setRpnScale(Number(d.settings.rpn_scale)); }
    } catch { /* defaults are fine */ }
  };
  useEffect(() => { fetchRisks(''); fetchDomains(); fetchDepartments(); fetchSettings(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const statusMounted = useRef(false);
  useEffect(() => {
    if (!statusMounted.current) { statusMounted.current = true; return; }
    fetchRisks(selectedStatus);
  }, [selectedStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Change a risk's category (list menu, side panel, or a drop on the board) ──
  const setCategory = async (risk, domainId) => {
    if (!domainId) return;
    const before = risks;
    setRisks((rs) => rs.map((r) => (r.id === risk.id ? { ...r, domain_id: domainId, domain_name: domains.find((d) => d.id === domainId)?.name || r.domain_name, domain_source: 'user' } : r)));
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/risks/${risk.id}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ domain_id: domainId }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'The category could not be changed.');
      setNotice(risk.domain_id === domainId ? `Category confirmed for "${risk.risk_title}".` : `"${risk.risk_title}" moved to ${domains.find((d) => d.id === domainId)?.name}.`);
      setTimeout(() => setNotice(''), 3500);
    } catch (e) { setRisks(before); setNotice(e.message); setTimeout(() => setNotice(''), 5000); }
  };

  // ── Derived: filter, group under umbrellas, then by category ──
  const now = new Date();
  const qNow = quarterLabelOf(now);
  const owners = useMemo(() => {
    const m = new Map();
    for (const r of risks) for (const n of (r.owner_names?.length ? r.owner_names : [r.owner_name]).filter(Boolean)) m.set(n, (m.get(n) || 0) + 1);
    return [...m.keys()].sort();
  }, [risks]);

  const childrenByParent = useMemo(() => {
    const m = {};
    for (const r of risks) if (r.parent_risk_id) (m[r.parent_risk_id] = m[r.parent_risk_id] || []).push(r);
    return m;
  }, [risks]);

  const worstChild = (kids) => { let best = null, bestScore = -1; for (const c of kids) { const s = computeRiskLevel(methodology, c, rpnScale).score || 0; if (s > bestScore) { bestScore = s; best = c; } } return best; };

  const rows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return risks.filter((r) => !r.parent_risk_id).map((top) => {
      const kids = childrenByParent[top.id] || [];
      const isUmbrella = top.is_umbrella || kids.length > 0;
      const level = computeRiskLevel(methodology, (isUmbrella && kids.length) ? (worstChild(kids) || top) : top, rpnScale);
      let verdict;
      if (isUmbrella && kids.length) {
        const inCount = kids.filter((k) => updateVerdict(k, now).k === 'current').length;
        const allIn = inCount === kids.length;
        verdict = top.status === 'closed' ? { k: 'closed', label: 'Closed', tone: 'bg-slate-100 text-slate-500' }
          : { k: allIn ? 'current' : 'awaiting', label: `${inCount}/${kids.length} updated this quarter`, tone: allIn ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800' };
      } else verdict = updateVerdict(top, now);
      return { top, kids, isUmbrella, level, verdict };
    }).filter(({ top, kids, level, verdict }) => {
      if (q) {
        const hay = [top.risk_title, top.risk_description, top.department_name, (top.department_names || []).join(' '), ownerText(top), top.domain_name, ...kids.map((k) => k.risk_title)].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (selectedDepartment && !(top.department_id === selectedDepartment || (top.department_ids || []).includes(selectedDepartment))) return false;
      if (selectedTier && level.band !== `Tier ${selectedTier}`) return false;
      if (selectedOwner && !ownerText(top).split(', ').includes(selectedOwner)) return false;
      if (scope === 'attention') {
        const needs = verdict.k === 'due' || verdict.k === 'awaiting' || ['Critical', 'High', 'Tier A'].includes(level.band) || kids.some((k) => ['due', 'awaiting'].includes(updateVerdict(k, now).k));
        if (!needs) return false;
      }
      return true;
    });
  }, [risks, childrenByParent, searchTerm, selectedDepartment, selectedTier, selectedOwner, scope, methodology, rpnScale]); // eslint-disable-line react-hooks/exhaustive-deps

  const sections = useMemo(() => {
    const list = [...domains].sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || a.name.localeCompare(b.name)).map((d) => ({ ...d, rows: [] }));
    const none = { ...UNCATEGORIZED, rows: [] };
    for (const row of rows) {
      const s = list.find((d) => d.id === row.top.domain_id);
      (s || none).rows.push(row);
    }
    for (const s of list) s.rows.sort((a, b) => (BAND_RANK[b.level.band] || 0) - (BAND_RANK[a.level.band] || 0) || a.top.risk_title.localeCompare(b.top.risk_title));
    const out = list.filter((s) => s.rows.length || scope === 'all');
    if (none.rows.length) out.push(none);
    return out;
  }, [domains, rows, scope]);

  const totals = useMemo(() => {
    const tops = risks.filter((r) => !r.parent_risk_id);
    const all = tops.map((t) => ({ level: computeRiskLevel(methodology, t, rpnScale), verdict: updateVerdict(t, now) }));
    return {
      onRegister: tops.length,
      awaiting: all.filter((x) => ['awaiting', 'due'].includes(x.verdict.k)).length,
      updated: all.filter((x) => x.verdict.k === 'current').length,
      tierA: all.filter((x) => ['Tier A', 'Critical'].includes(x.level.band)).length,
      categories: new Set(tops.map((t) => t.domain_id).filter(Boolean)).size,
      placedBySystem: tops.filter((t) => t.domain_source === 'system').length,
    };
  }, [risks, methodology, rpnScale]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCollapsed = (id) => setCollapsed((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleExpand = (id) => setExpanded((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const clearFilters = () => { setSearchTerm(''); setSelectedDepartment(''); setSelectedTier(''); setSelectedOwner(''); setSelectedStatus(''); };

  // ── New risk ──
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createForm, setCreateForm] = useState({ risk_title: '', risk_description: '', domain_id: '', children: [] });
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const suggestTimer = useRef(null);
  const openCreate = (domainId = '') => { setCreateForm({ risk_title: '', risk_description: '', domain_id: domainId, children: [] }); setCategoryTouched(!!domainId); setSuggested(null); setCreateError(''); setShowCreate(true); };
  // As the title is typed, the category the words point to is offered; the person decides.
  useEffect(() => {
    if (!showCreate || categoryTouched || !createForm.risk_title.trim()) return undefined;
    clearTimeout(suggestTimer.current);
    suggestTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API_BASE}/risk-assessment/risks/suggest-category`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: createForm.risk_title, description: createForm.risk_description }) });
        const d = await r.json();
        if (d.success && d.domain_id) { setSuggested(d.name); setCreateForm((f) => (f.domain_id ? f : { ...f, domain_id: d.domain_id })); }
      } catch { /* no suggestion is fine */ }
    }, 450);
    return () => clearTimeout(suggestTimer.current);
  }, [createForm.risk_title, createForm.risk_description, showCreate, categoryTouched]);

  const handleCreate = async () => {
    if (!createForm.risk_title.trim() || !createForm.domain_id) return;
    setCreating(true); setCreateError('');
    try {
      const children = createForm.children.filter((c) => c.risk_title.trim()).map((c) => ({ risk_title: c.risk_title.trim(), risk_description: (c.risk_description || '').trim() || null }));
      const body = { risk_title: createForm.risk_title.trim(), risk_description: createForm.risk_description.trim() || null, domain_id: createForm.domain_id, is_umbrella: children.length > 0, children };
      const res = await fetch(`${API_BASE}/risk-assessment/risks`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'The risk could not be created.');
      if (data.risk?.is_umbrella && data.risk?.id) setExpanded((prev) => new Set(prev).add(data.risk.id));
      setShowCreate(false);
      fetchRisks(selectedStatus);
    } catch (e) { setCreateError(e.message); } finally { setCreating(false); }
  };

  // ── Drag between board columns ──
  const onDropTo = (domainId) => { const r = dragging; setDragging(null); setDropTarget(null); if (r && r.domain_id !== domainId) setCategory(r, domainId); };

  const openRisk = openRiskId ? risks.find((r) => r.id === openRiskId) : null;
  const openRow = openRisk ? rows.find((x) => x.top.id === openRisk.id) || { top: openRisk, kids: childrenByParent[openRisk.id] || [], level: computeRiskLevel(methodology, openRisk, rpnScale), verdict: updateVerdict(openRisk, now) } : null;

  if (loading && !risks.length) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  const CategorySelect = ({ value, onChange, className = '', small = false }) => (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} onClick={(e) => e.stopPropagation()}
      className={`${small ? 'text-[11px] py-0.5 px-1.5' : 'text-sm py-2 px-3'} rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-slate-300 focus:ring-2 focus:ring-orange-300 focus:outline-none ${className}`}>
      {!value && <option value="">Choose a category</option>}
      {domains.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
    </select>
  );

  const RiskRow = ({ row, inSection }) => {
    const { top, kids, isUmbrella, level, verdict } = row;
    const isOpen = expanded.has(top.id);
    return (
      <div className="group">
        <div className={`flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer ${openRiskId === top.id ? 'bg-orange-50/60' : ''}`}
          draggable onDragStart={() => setDragging(top)} onDragEnd={() => { setDragging(null); setDropTarget(null); }}
          onClick={() => setOpenRiskId(top.id)}>
          <button className="text-slate-300 hover:text-slate-600 w-4 flex-none" onClick={(e) => { e.stopPropagation(); if (kids.length) toggleExpand(top.id); }} title={kids.length ? (isOpen ? 'Hide sub-risks' : 'Show sub-risks') : ''}>
            {kids.length ? (isOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />) : <span className="inline-block w-4" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[13.5px] font-medium text-slate-900 truncate" title={top.risk_title}>{top.risk_title}</span>
              {isUmbrella && <span className="px-1.5 py-px text-[10px] font-semibold rounded bg-indigo-50 text-indigo-700 whitespace-nowrap">Umbrella · {kids.length}</span>}
              {top.domain_source === 'system' && <span className="px-1.5 py-px text-[10px] font-semibold rounded bg-slate-100 text-slate-500 whitespace-nowrap" title="The category was placed from the risk's words. Confirm it or change it.">Placed automatically</span>}
            </div>
            {top.risk_description && <div className="text-xs text-slate-500 truncate" title={top.risk_description}>{top.risk_description}</div>}
          </div>
          {!inSection && <span className={`hidden lg:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${colorFor(top.domain_name).soft}`}><span className={`h-1.5 w-1.5 rounded-full ${colorFor(top.domain_name).dot}`} />{top.domain_name || 'No category'}</span>}
          <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-500 max-w-[11rem]"><Avatar name={ownerText(top)} /><span className="truncate">{ownerText(top)}</span></div>
          <VerdictChip v={verdict} />
          <span className="hidden sm:inline-flex"><StatusPill s={top.status} /></span>
          <LevelPill level={level} />
          <span className="hidden xl:inline-flex opacity-0 group-hover:opacity-100 transition-opacity"><CategorySelect small value={top.domain_id} onChange={(v) => setCategory(top, v)} /></span>
        </div>
        {isOpen && kids.length > 0 && (
          <div className="pl-14 pr-4 pb-2 divide-y divide-slate-100 bg-slate-50/50">
            {kids.map(k => {
              const kl = computeRiskLevel(methodology, k, rpnScale); const kv = updateVerdict(k, now);
              return (
                <div key={k.id} className="flex items-center gap-3 py-2 hover:bg-white cursor-pointer" onClick={() => onOpenRisk && onOpenRisk(k.id)}>
                  <span className="text-slate-300 flex-none">↳</span>
                  <div className="flex-1 min-w-0"><div className="text-[13px] text-slate-800 truncate">{k.risk_title}</div><div className="text-xs text-slate-400 truncate">{ownerText(k)}{k.last_update_at ? ` · last update ${fmtDateTime(k.last_update_at)}` : ' · no updates yet'}</div></div>
                  <VerdictChip v={kv} /><LevelPill level={kl} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 rounded-2xl px-6 py-5 text-white mb-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Risk Register</h1>
            <p className="text-sm text-slate-300 mt-1">Every risk in its category. Open a risk for its latest update, sub-risks and scores; drag it to move it. Update requests are sent from Workstream.</p>
          </div>
          <div className="flex items-center gap-2 flex-none">
            <button onClick={() => openCreate('')} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 hover:opacity-90 text-white text-sm font-semibold shadow-sm"><PlusIcon className="h-4 w-4" />New risk</button>
          </div>
        </div>

        {/* Counts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[
            ['On the register', totals.onRegister, `${totals.categories} categor${totals.categories === 1 ? 'y' : 'ies'} in use`],
            [`Awaiting ${qNow}`, totals.awaiting, 'updates not yet in', totals.awaiting ? 'text-amber-600' : ''],
            ['Updated', totals.updated, 'this period', 'text-emerald-600'],
            ['Tier A', totals.tierA, 'highest level', totals.tierA ? 'text-red-600' : ''],
          ].map(([label, n, sub, tone]) => (
            <div key={label} className="rounded-2xl bg-white ring-1 ring-slate-200 px-4 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
              <div className={`text-2xl font-bold mt-0.5 ${tone || 'text-slate-900'}`}>{n}</div>
              <div className="text-[11px] text-slate-400">{sub}</div>
            </div>
          ))}
        </div>

        {totals.placedBySystem > 0 && (
          <div className="mb-4 rounded-xl bg-orange-50 ring-1 ring-orange-200 px-4 py-2.5 text-[13px] text-orange-900">
            {totals.placedBySystem} risk{totals.placedBySystem === 1 ? ' was' : 's were'} placed in a category automatically from {totals.placedBySystem === 1 ? 'its' : 'their'} own words. Open each one and confirm the category, or move it. The tag disappears once you do.
          </div>
        )}

        {/* Toolbar */}
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 px-3 py-2.5 mb-4 flex flex-col lg:flex-row gap-2 lg:items-center">
          <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
            {[['attention', 'Needs attention'], ['all', 'All risks']].map(([k, label]) => (
              <button key={k} onClick={() => setScope(k)} className={`px-3 py-1.5 rounded-md text-sm font-medium ${scope === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>{label}</button>
            ))}
          </div>
          <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
            <button onClick={() => setLayout('list')} title="List" className={`px-2.5 py-1.5 rounded-md ${layout === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}><Bars3BottomLeftIcon className="h-4 w-4" /></button>
            <button onClick={() => setLayout('board')} title="Board" className={`px-2.5 py-1.5 rounded-md ${layout === 'board' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}><Squares2X2Icon className="h-4 w-4" /></button>
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" placeholder="Search risks" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 pr-3 py-2 w-full rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-orange-300 focus:outline-none" />
          </div>
          <div className="flex flex-wrap gap-2">
            <select value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"><option value="">All departments</option>{departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select>
            <select value={selectedOwner} onChange={(e) => setSelectedOwner(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"><option value="">All owners</option>{owners.map((o) => <option key={o} value={o}>{o}</option>)}</select>
            <select value={selectedTier} onChange={(e) => setSelectedTier(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"><option value="">All tiers</option>{['A', 'B', 'C'].map((t) => <option key={t} value={t}>Tier {t}</option>)}</select>
            <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} title="Closed risks leave the register. Choose Closed to search the archive." className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">Open register</option><option value="monitoring">Monitoring</option><option value="mitigating">Mitigating</option><option value="accepted">Accepted</option><option value="transferred">Transferred</option><option value="closed">Closed (archive)</option><option value="all">All, including closed</option>
            </select>
            {(searchTerm || selectedDepartment || selectedTier || selectedOwner || selectedStatus) && <button onClick={clearFilters} className="px-3 py-2 text-sm text-slate-500 hover:text-slate-800">Clear</button>}
          </div>
        </div>

        {error && <div className="mb-4 rounded-xl bg-rose-50 ring-1 ring-rose-200 px-4 py-2.5 text-sm text-rose-700">{error}</div>}
        {notice && <div className="mb-4 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 px-4 py-2.5 text-sm text-emerald-800">{notice}</div>}

        {/* List: one section per category */}
        {layout === 'list' && (
          <div className="space-y-3">
            {sections.map((s) => {
              const c = colorFor(s.name); const isCollapsed = collapsed.has(s.id);
              const tierA = s.rows.filter((r) => ['Tier A', 'Critical'].includes(r.level.band)).length;
              const awaiting = s.rows.filter((r) => ['awaiting', 'due'].includes(r.verdict.k)).length;
              return (
                <section key={s.id} className={`rounded-2xl bg-white ring-1 ring-slate-200 overflow-hidden ${dropTarget === s.id ? 'ring-2 ring-orange-400' : ''}`}
                  onDragOver={(e) => { if (dragging && s.id !== '__none__') { e.preventDefault(); setDropTarget(s.id); } }} onDragLeave={() => setDropTarget(null)} onDrop={() => s.id !== '__none__' && onDropTo(s.id)}>
                  <header className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 cursor-pointer select-none" onClick={() => toggleCollapsed(s.id)}>
                    <button className="text-slate-400">{isCollapsed ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}</button>
                    <span className={`h-2.5 w-2.5 rounded-full ${c.dot}`} />
                    <h2 className="text-[13px] font-semibold text-slate-800">{s.name}</h2>
                    <span className="text-xs text-slate-400">{s.rows.length} risk{s.rows.length === 1 ? '' : 's'}{tierA ? ` · ${tierA} Tier A` : ''}{awaiting ? ` · ${awaiting} awaiting update` : ''}</span>
                    <span className="flex-1" />
                    {s.id !== '__none__' && <button onClick={(e) => { e.stopPropagation(); openCreate(s.id); }} className="text-xs font-medium text-slate-500 hover:text-orange-600 inline-flex items-center gap-1"><PlusIcon className="h-3.5 w-3.5" />Add</button>}
                  </header>
                  {!isCollapsed && (
                    <div className="divide-y divide-slate-100">
                      {s.rows.map((row) => <RiskRow key={row.top.id} row={row} inSection />)}
                      {s.rows.length === 0 && <div className="px-4 py-3 text-xs text-slate-400">Nothing in this category{scope === 'attention' ? ' needs attention' : ''}.</div>}
                    </div>
                  )}
                </section>
              );
            })}
            {sections.length === 0 && (
              <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-12 text-center">
                <div className="text-base font-semibold text-slate-800">{scope === 'attention' && risks.length ? 'Nothing needs attention' : 'No risks yet'}</div>
                <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{scope === 'attention' && risks.length ? `Every risk has its ${qNow} update in and nothing is running at Tier A. Switch to All risks to browse.` : 'Create the first risk with New risk. It goes straight into its category.'}</p>
              </div>
            )}
          </div>
        )}

        {/* Board: one column per category */}
        {layout === 'board' && (
          <div className="flex gap-3 overflow-x-auto pb-4 -mx-1 px-1">
            {sections.filter((s) => s.id !== '__none__' || s.rows.length).map((s) => {
              const c = colorFor(s.name);
              return (
                <div key={s.id} className={`w-72 flex-none rounded-2xl bg-slate-100/70 ring-1 ${dropTarget === s.id ? 'ring-orange-400 ring-2' : 'ring-slate-200'} p-2`}
                  onDragOver={(e) => { if (dragging && s.id !== '__none__') { e.preventDefault(); setDropTarget(s.id); } }} onDragLeave={() => setDropTarget(null)} onDrop={() => s.id !== '__none__' && onDropTo(s.id)}>
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <span className={`h-2.5 w-2.5 rounded-full ${c.dot}`} /><span className="text-[13px] font-semibold text-slate-800 truncate">{s.name}</span><span className="text-xs text-slate-400">{s.rows.length}</span>
                    <span className="flex-1" />{s.id !== '__none__' && <button onClick={() => openCreate(s.id)} className="text-slate-400 hover:text-orange-600"><PlusIcon className="h-4 w-4" /></button>}
                  </div>
                  <div className="space-y-2 min-h-[4rem]">
                    {s.rows.map(({ top, kids, level, verdict }) => (
                      <div key={top.id} draggable onDragStart={() => setDragging(top)} onDragEnd={() => { setDragging(null); setDropTarget(null); }} onClick={() => setOpenRiskId(top.id)}
                        className={`rounded-xl bg-white ring-1 ring-slate-200 p-3 cursor-grab hover:shadow-md transition-shadow ${openRiskId === top.id ? 'ring-2 ring-orange-300' : ''}`}>
                        <div className="text-[13px] font-medium text-slate-900 leading-snug">{top.risk_title}</div>
                        {kids.length > 0 && <div className="text-[11px] text-indigo-700 mt-0.5">Umbrella · {kids.length} sub-risks</div>}
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap"><LevelPill level={level} /><VerdictChip v={verdict} /></div>
                        <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-500"><Avatar name={ownerText(top)} /><span className="truncate">{ownerText(top)}</span></div>
                      </div>
                    ))}
                    {s.rows.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 p-3 text-center text-xs text-slate-400">Drop a risk here</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Side panel */}
        {openRow && (
          <div className="fixed inset-0 z-40" onClick={() => setOpenRiskId(null)}>
            <div className="absolute inset-0 bg-slate-900/20" />
            <aside className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-slate-100 px-6 py-4 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1"><LevelPill level={openRow.level} /><StatusPill s={openRow.top.status} /><VerdictChip v={openRow.verdict} /></div>
                  <h2 className="text-lg font-semibold text-slate-900 leading-snug">{openRow.top.risk_title}</h2>
                </div>
                <button onClick={() => setOpenRiskId(null)} className="text-slate-400 hover:text-slate-700"><XMarkIcon className="h-5 w-5" /></button>
              </div>
              <div className="px-6 py-5 space-y-5 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Category</div>
                    <CategorySelect value={openRow.top.domain_id} onChange={(v) => setCategory(openRow.top, v)} className="w-full" />
                    {openRow.top.domain_source === 'system' && <button onClick={() => setCategory(openRow.top, openRow.top.domain_id)} className="mt-1.5 text-xs font-medium text-orange-700 hover:text-orange-800">Placed automatically. Looks right, confirm it</button>}
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Owner</div>
                    <div className="flex items-center gap-2 text-slate-800"><Avatar name={ownerText(openRow.top)} />{ownerText(openRow.top)}</div>
                    {(openRow.top.department_names?.length || openRow.top.department_name) && <div className="text-xs text-slate-500 mt-1">{(openRow.top.department_names || [openRow.top.department_name]).join(', ')}</div>}
                  </div>
                </div>
                {openRow.top.risk_description && <div><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">What could go wrong</div><p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{openRow.top.risk_description}</p></div>}
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Latest update</div>
                  {openRow.top.last_update_note || openRow.top.last_update_at
                    ? <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-slate-700">{openRow.top.last_update_note || '(status change)'}<div className="text-xs text-slate-400 mt-1">{openRow.top.last_update_by ? `${openRow.top.last_update_by} · ` : ''}{fmtDateTime(openRow.top.last_update_at)}</div></div>
                    : <div className="text-slate-400">No updates recorded yet.</div>}
                </div>
                {openRow.kids.length > 0 && (
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Sub-risks</div>
                    <div className="rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
                      {openRow.kids.map((k) => <div key={k.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer" onClick={() => onOpenRisk && onOpenRisk(k.id)}><span className="flex-1 truncate text-slate-800">{k.risk_title}</span><VerdictChip v={updateVerdict(k, now)} /><LevelPill level={computeRiskLevel(methodology, k, rpnScale)} /></div>)}
                    </div>
                  </div>
                )}
                {openRow.top.current_controls && <div><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Controls in place</div><p className="text-slate-700 whitespace-pre-wrap">{openRow.top.current_controls}</p></div>}
                <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                  {openRow.level.score != null && <span>Score {openRow.level.score}</span>}
                  {openRow.top.treatment && <span className="capitalize">Treatment: {openRow.top.treatment}</span>}
                  {openRow.top.monthly_update_required && <span className="text-red-600 font-medium">Monthly updates required</span>}
                  {openRow.top.received_date && <span>Received {new Date(openRow.top.received_date).toLocaleDateString()}</span>}
                </div>
                <div className="pt-2 flex items-center gap-3">
                  <button onClick={() => onOpenRisk && onOpenRisk(openRow.top.id)} className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">Open full record</button>
                  <span className="text-xs text-slate-400">Scores, mitigation plans and history live on the full record.</span>
                </div>
              </div>
            </aside>
          </div>
        )}

        {/* New risk */}
        {showCreate && (
          <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={() => !creating && setShowCreate(false)}>
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between"><h3 className="text-lg font-semibold text-slate-900">New risk</h3><button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-700"><XMarkIcon className="h-5 w-5" /></button></div>
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{createForm.children.length ? 'Umbrella risk' : 'Risk'}</label>
                  <input autoFocus type="text" value={createForm.risk_title} onChange={(e) => setCreateForm((f) => ({ ...f, risk_title: e.target.value }))} placeholder="What could go wrong, in one line" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-orange-300 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <CategorySelect value={createForm.domain_id} onChange={(v) => { setCategoryTouched(true); setCreateForm((f) => ({ ...f, domain_id: v })); }} className="w-full" />
                  <div className="text-xs text-slate-400 mt-1">{suggested && !categoryTouched ? `Suggested from the words: ${suggested}. Change it if it is not right.` : 'Every risk sits in one category; the register is organized by it.'}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea value={createForm.risk_description} onChange={(e) => setCreateForm((f) => ({ ...f, risk_description: e.target.value }))} rows={3} placeholder="What is at stake, for whom, and why now." className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-orange-300 focus:outline-none" />
                </div>
                {createForm.children.length > 0 && (
                  <div className="space-y-2 border-t border-slate-100 pt-4">
                    <div className="text-sm font-medium text-slate-700">Sub-risks ({createForm.children.length}), same category</div>
                    {createForm.children.map((c, i) => (
                      <div key={i} className="rounded-xl ring-1 ring-slate-200 p-3 space-y-2 relative">
                        <button onClick={() => setCreateForm((f) => ({ ...f, children: f.children.filter((_, idx) => idx !== i) }))} className="absolute top-2 right-2 text-xs text-slate-400 hover:text-rose-600">Remove</button>
                        <input type="text" value={c.risk_title} onChange={(e) => setCreateForm((f) => ({ ...f, children: f.children.map((x, idx) => (idx === i ? { ...x, risk_title: e.target.value } : x)) }))} placeholder={`Sub-risk ${i + 1}`} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                        <textarea value={c.risk_description} onChange={(e) => setCreateForm((f) => ({ ...f, children: f.children.map((x, idx) => (idx === i ? { ...x, risk_description: e.target.value } : x)) }))} rows={2} placeholder="Description" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                      </div>
                    ))}
                  </div>
                )}
                <button onClick={() => setCreateForm((f) => ({ ...f, children: [...f.children, { risk_title: '', risk_description: '' }] }))} className="text-sm font-medium text-indigo-600 hover:text-indigo-700">+ Add a sub-risk</button>
                {createError && <div className="text-sm text-rose-600 font-medium">{createError}</div>}
              </div>
              <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-50 text-sm">Cancel</button>
                <button onClick={handleCreate} disabled={creating || !createForm.risk_title.trim() || !createForm.domain_id} className="px-5 py-2 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 hover:opacity-90 text-white text-sm font-semibold disabled:opacity-50">{creating ? 'Creating' : (createForm.children.length ? 'Create umbrella and sub-risks' : 'Create risk')}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskLibrary;
